import { useEffect, useCallback } from "react";

// Single Calendly event used across the site.
export const CALENDLY_URL = "https://calendly.com/jenna-thriveandmother/30min";

// Load Calendly's widget script + CSS once, lazily.
function loadCalendly() {
  if (typeof window === "undefined") return;
  if (!document.getElementById("calendly-css")) {
    const link = document.createElement("link");
    link.id = "calendly-css";
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);
  }
  if (!document.getElementById("calendly-js") && !window.Calendly) {
    const s = document.createElement("script");
    s.id = "calendly-js";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }
}

// Derive a stable utmSource label from the current page path.
function deriveUtmSource() {
  if (typeof window === "undefined") return "unknown";
  const path = window.location.pathname;
  if (path === "/") return "home";
  if (path.startsWith("/lead")) return "work-with-jenna";
  if (path.startsWith("/method")) return "method";
  if (path.startsWith("/about")) return "about";
  if (path.startsWith("/shop")) return "resources";
  if (path.startsWith("/downloads")) return "downloads";
  if (path.startsWith("/tools")) return "tools";
  if (path.startsWith("/free-preview")) return "free-preview";
  return path.replace(/^\//, "").replace(/\//g, "-") || "unknown";
}

// Append utm_* query params to a URL string.
function appendUtms(url, utm) {
  try {
    const u = new URL(url);
    Object.entries(utm).forEach(([k, v]) => {
      if (v) u.searchParams.set(k, v);
    });
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Returns an `open(options?)` function. Options:
 *   - medium:  where on the page (e.g., "hero", "nav", "banner", "conversion", "offering-card")
 *   - content: specific button variant (e.g., "primary-cta", "mobile-menu")
 *   - source:  override the auto-derived page source
 *   - url:     override the Calendly URL
 *
 * utmCampaign is always "discovery-call".
 */
export function useCalendly() {
  useEffect(() => {
    loadCalendly();
  }, []);
  return useCallback((options = {}) => {
    const {
      url = CALENDLY_URL,
      source,
      medium = "cta",
      content,
    } = typeof options === "string" ? { url: options } : options;

    const utm = {
      utmCampaign: "discovery-call",
      utmSource: source || deriveUtmSource(),
      utmMedium: medium,
      utmContent: content || undefined,
    };

    if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
      window.Calendly.initPopupWidget({ url, utm });
    } else {
      // fallback: open in a new tab with utm_* query params if the script hasn't loaded
      const fallbackUrl = appendUtms(url, {
        utm_campaign: utm.utmCampaign,
        utm_source: utm.utmSource,
        utm_medium: utm.utmMedium,
        utm_content: utm.utmContent,
      });
      window.open(fallbackUrl, "_blank", "noopener");
    }
  }, []);
}

// Drop-in button component. Renders whatever `children` you pass and opens Calendly on click.
export function CalendlyButton({
  children,
  className = "",
  testId,
  url = CALENDLY_URL,
  medium = "cta",
  content,
  as: As = "button",
}) {
  const openCalendly = useCalendly();
  return (
    <As
      type={As === "button" ? "button" : undefined}
      onClick={(e) => {
        e.preventDefault();
        openCalendly({ url, medium, content });
      }}
      data-testid={testId}
      className={className}
    >
      {children}
    </As>
  );
}
